/**
 * MODULE: domain/pages/frontend/components/editor/PagesActionButtonBlockView.tsx
 * PURPOSE: CTA button block linking to an external URL or workspace page
 */

import { NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'flowbite-react';
import { HiOutlinePencil } from 'react-icons/hi';
import { useIsPagesPublicView } from '../../context/PagesEditorViewModeContext';
import {
  actionButtonContentToAttrs,
  actionButtonVariantClass,
  buildActionButtonHref,
  readActionButtonFromAttrs,
} from '../../utils/pageActionButtonUtils';
import { ActionButtonModal } from './ActionButtonModal';

export function PagesActionButtonBlockView({ node, selected, updateAttributes, editor }: NodeViewProps) {
  const navigate = useNavigate();
  const isPublicView = useIsPagesPublicView();
  const editable = editor.isEditable && !isPublicView;
  const [configOpen, setConfigOpen] = useState(false);

  const content = readActionButtonFromAttrs(node.attrs as Record<string, unknown>);
  const workspaceId = typeof node.attrs.workspaceId === 'string' ? node.attrs.workspaceId : '';
  const hostPageId = typeof node.attrs.hostPageId === 'string' ? node.attrs.hostPageId : '';

  if (!content) {
    return (
      <NodeViewWrapper className="my-3" data-testid="pages-action-button-block">
        <div className="rounded-lg border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-500 dark:border-gray-700">
          Invalid button — configure label and link.
          {editable ? (
            <Button size="xs" color="light" className="ml-2" onClick={() => setConfigOpen(true)}>
              Configure
            </Button>
          ) : null}
        </div>
        {editable && configOpen ? (
          <ActionButtonModal
            open={configOpen}
            workspaceId={workspaceId}
            currentPageId={hostPageId}
            initial={null}
            onClose={() => setConfigOpen(false)}
            onSubmit={(next) => updateAttributes(actionButtonContentToAttrs(next, { workspaceId, pageId: hostPageId }))}
          />
        ) : null}
      </NodeViewWrapper>
    );
  }

  const href =
    content.link_type === 'page' && isPublicView ? null : buildActionButtonHref(content);
  const variantClass = actionButtonVariantClass(content.variant);

  const handleClick = (event: React.MouseEvent) => {
    if (editable || !href) {
      event.preventDefault();
      return;
    }
    if (content.link_type === 'page') {
      event.preventDefault();
      navigate(href);
    }
  };

  const buttonEl = href ? (
    <a
      href={href}
      target={content.open_in_new_tab ? '_blank' : undefined}
      rel={content.open_in_new_tab ? 'noopener noreferrer' : undefined}
      onClick={handleClick}
      className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium transition-colors ${variantClass}`}
      data-testid="pages-action-button-link"
    >
      {content.label}
    </a>
  ) : (
    <span
      className={`inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium opacity-60 ${variantClass}`}
      data-testid="pages-action-button-link"
    >
      {content.label}
    </span>
  );

  return (
    <NodeViewWrapper
      className={`my-3 flex flex-wrap items-center gap-2 ${selected ? 'ring-2 ring-blue-400 ring-offset-2 dark:ring-offset-gray-950' : ''}`}
      data-testid="pages-action-button-block"
    >
      {buttonEl}
      {editable ? (
        <Button
          size="xs"
          color="light"
          aria-label="Edit action button"
          data-testid="pages-action-button-edit"
          onClick={() => setConfigOpen(true)}
        >
          <HiOutlinePencil className="h-3.5 w-3.5" aria-hidden />
        </Button>
      ) : null}
      {isPublicView && content.link_type === 'page' ? (
        <span className="text-[11px] text-gray-500 dark:text-gray-400" data-testid="pages-action-button-public-note">
          Sign in to Brazn to open linked pages.
        </span>
      ) : null}
      {editable && configOpen ? (
        <ActionButtonModal
          open={configOpen}
          workspaceId={workspaceId || content.workspace_id || ''}
          currentPageId={hostPageId}
          initial={content}
          onClose={() => setConfigOpen(false)}
          onSubmit={(next) =>
            updateAttributes(
              actionButtonContentToAttrs(next, {
                workspaceId: workspaceId || content.workspace_id || '',
                pageId: hostPageId,
              })
            )
          }
        />
      ) : null}
    </NodeViewWrapper>
  );
}
